import { useCallback, useEffect, useState } from 'react';
import { callLogApi } from '../services/api/call-log.api';
import { CallTraceSummary } from '../services/api/call-log.types';
import { getDefaultDateRange } from '../components/activity-log/call-activity-log.utils';

const PAGE_SIZE = 25;

interface UseCallActivityTracesOptions {
  enabled?: boolean;
}

export function useCallActivityTraces({ enabled = true }: UseCallActivityTracesOptions = {}) {
  const [fromDate, setFromDate] = useState(() => {
    const { fromDate: initialFromDate } = getDefaultDateRange();
    return initialFromDate;
  });
  const [toDate, setToDate] = useState(() => {
    const { toDate: initialToDate } = getDefaultDateRange();
    return initialToDate;
  });
  const [callerSearch, setCallerSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<CallTraceSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTraceId, setExpandedTraceId] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setError(null);

    void callLogApi
      .listTraces({
        fromDate,
        toDate,
        callerSearch: callerSearch.trim() || undefined,
        limit: PAGE_SIZE,
        offset,
      })
      .then((response) => {
        if (cancelled) {
          return;
        }

        setItems(response.data.items);
        setTotal(response.data.total);
        setHasMore(response.data.hasMore);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }

        setItems([]);
        setTotal(0);
        setHasMore(false);
        setError(err instanceof Error ? err.message : 'Failed to load activity logs');
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, fromDate, toDate, callerSearch, offset, refreshNonce]);

  const refresh = useCallback(() => {
    setExpandedTraceId(null);
    setRefreshNonce((current) => current + 1);
  }, []);

  const applyFilters = useCallback(
    (nextFromDate: string, nextToDate: string, nextCallerSearch: string) => {
      setFromDate(nextFromDate);
      setToDate(nextToDate);
      setCallerSearch(nextCallerSearch.trim());
      setOffset(0);
      setExpandedTraceId(null);
    },
    []
  );

  const clearFilters = useCallback(() => {
    const nextRange = getDefaultDateRange();
    setFromDate(nextRange.fromDate);
    setToDate(nextRange.toDate);
    setCallerSearch('');
    setOffset(0);
    setExpandedTraceId(null);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setOffset((current) => Math.max(0, current - PAGE_SIZE));
    setExpandedTraceId(null);
  }, []);

  const goToNextPage = useCallback(() => {
    if (hasMore) {
      setOffset((current) => current + PAGE_SIZE);
      setExpandedTraceId(null);
    }
  }, [hasMore]);

  const toggleExpanded = useCallback((callTraceId: string) => {
    setExpandedTraceId((current) => (current === callTraceId ? null : callTraceId));
  }, []);

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + items.length, total);

  return {
    fromDate,
    toDate,
    callerSearch,
    items,
    total,
    hasMore,
    isLoading,
    error,
    expandedTraceId,
    pageStart,
    pageEnd,
    pageSize: PAGE_SIZE,
    applyFilters,
    clearFilters,
    goToPreviousPage,
    goToNextPage,
    toggleExpanded,
    refresh,
  };
}

export type CallActivityTracesState = ReturnType<typeof useCallActivityTraces>;
